import * as React from 'react';
import { cn } from '@/lib/utils';

export type RubberStampTone = 'teal' | 'coral' | 'ink';

export interface RubberStampProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone: RubberStampTone;
  tilt?: number;
}

const palette: Record<RubberStampTone, string> = {
  teal: 'border-fd-teal/60 text-fd-teal',
  coral: 'border-fd-coral/60 text-fd-coral',
  ink: 'border-fd-text/60 text-fd-text',
};

const stampSrc: Record<string, string> = {
  'repo-native': '/character-system/scribble/stamps/stamp-repo-native-v1.png',
  ready: '/character-system/scribble/stamps/stamp-ready-v1.png',
  pending: '/character-system/scribble/stamps/stamp-pending-v1.png',
  draft: '/character-system/scribble/stamps/stamp-draft-v1.png',
  accepted: '/character-system/scribble/stamps/stamp-accepted-v1.png',
  review: '/character-system/scribble/stamps/stamp-review-v1.png',
  blocked: '/character-system/scribble/stamps/stamp-blocked-v1.png',
  imported: '/character-system/scribble/stamps/stamp-imported-v1.png',
};

function stampKey(children: React.ReactNode) {
  return React.Children.toArray(children)
    .join('')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-');
}

export const RubberStamp = React.forwardRef<HTMLSpanElement, RubberStampProps>(
  ({ tone, tilt = -3, children, className, style, ...rest }, ref) => {
    const key = stampKey(children);
    const src = stampSrc[key];

    if (src) {
      return (
        <span
          ref={ref}
          role="img"
          aria-label={key.replace(/-/g, ' ')}
          className={cn('inline-block align-middle', className)}
          style={{ transform: `rotate(${tilt}deg)`, ...style }}
          {...rest}
        >
          <img
            src={src}
            alt=""
            aria-hidden
            className="block h-6 w-auto select-none"
            draggable={false}
          />
        </span>
      );
    }

    return (
      <span
        ref={ref}
        style={{
          transform: `rotate(${tilt}deg)`,
          display: 'inline-block',
          letterSpacing: '0.14em',
          ...style,
        }}
        className={cn(
          'inline-flex h-5 items-center rounded-[3px] border-[1.5px] bg-transparent px-1.5 font-geist-mono text-[9px] font-black uppercase leading-none',
          palette[tone],
          className,
        )}
        {...rest}
      >
        {children}
      </span>
    );
  },
);
RubberStamp.displayName = 'RubberStamp';
